import { tenantPortalOrigin } from './tenantSlug';

export const SA_IMPERSONATE_HASH_KEY = 'sa-impersonate';

export type SuperAdminImpersonationPayload = {
  session_token: string;
  tenant_id: number | string;
  user_id?: string | number | null;
  email: string;
  permissions?: string[];
  role?: string | null;
  slug?: string | null;
};

function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (padded.length % 4)) % 4;
  const base64 = padded + '='.repeat(padLength);
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeImpersonationPayload(payload: SuperAdminImpersonationPayload): string {
  return toBase64Url(JSON.stringify(payload));
}

export function decodeImpersonationPayload(raw: string): SuperAdminImpersonationPayload | null {
  try {
    const parsed = JSON.parse(fromBase64Url(raw)) as SuperAdminImpersonationPayload;
    if (!parsed?.session_token || parsed.tenant_id == null || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readImpersonationPayloadFromLocation(
  location: Pick<Location, 'hash'> = window.location
): SuperAdminImpersonationPayload | null {
  const hash = (location.hash || '').replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const raw = params.get(SA_IMPERSONATE_HASH_KEY);
  if (!raw) return null;
  return decodeImpersonationPayload(raw);
}

export function clearImpersonationHashFromLocation(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}`);
}

export function buildTenantImpersonationUrl(payload: SuperAdminImpersonationPayload): string {
  const slug = String(payload.slug ?? '').trim();
  const origin = tenantPortalOrigin(slug);
  if (!origin) {
    throw new Error('This tenant has no URL slug yet. Set a slug before impersonating.');
  }
  const encoded = encodeImpersonationPayload(payload);
  return `${origin}/login#${SA_IMPERSONATE_HASH_KEY}=${encoded}`;
}
